import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", email: "", password: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accept) { setError("Please accept the Terms and Privacy Policy to continue."); return; }
    setBusy(true);
    try {
      let ref;
      try { ref = localStorage.getItem("kidpath_ref") || undefined; } catch {}
      await register({ ...form, accept_terms: true, accept_privacy: true, ...(ref ? { ref } : {}) });
      try { localStorage.removeItem("kidpath_ref"); } catch {}
      navigate("/app/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testid="register-page" title="Start free" subtitle="Create your account and build a calm, personalized roadmap.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {error && (
          <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="register-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="first_name">First name</Label>
          <Input id="first_name" value={form.first_name} onChange={set("first_name")} required
                 data-testid="register-firstname-input" placeholder="Jamie" className="h-12 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required
                 data-testid="register-email-input" placeholder="you@example.com" className="h-12 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={8}
                 data-testid="register-password-input" placeholder="At least 8 characters" className="h-12 rounded-xl" />
        </div>
        <label className="flex items-start gap-3 text-sm text-kid-muted cursor-pointer">
          <Checkbox checked={accept} onCheckedChange={setAccept} data-testid="register-consent-checkbox" className="mt-0.5" />
          <span>I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-kid-teal font-semibold">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-kid-teal font-semibold">Privacy Policy</a>, and understand KidPath provides information and navigation, not medical, legal, or tax advice.</span>
        </label>
        <button type="submit" disabled={busy} className="kid-btn kid-btn-primary w-full h-12 text-base disabled:opacity-60" data-testid="register-submit-button">
          {busy ? "Creating account…" : "Create my account"}
        </button>
      </form>
      <p className="text-center text-sm text-kid-muted mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-kid-teal font-semibold" data-testid="go-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
